import React, { useState } from "react";
import { useProfile } from "./contexts/ProfileContext";
import { useSupabaseAuth } from "./contexts/SupabaseAuthContext";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { useUISounds } from "./hooks/useUISounds";
import { INTRO_CONTENT } from "./constants/introContent";
import ProfileAvatar from "./components/ProfileAvatar";
import { prepareSessionForProfile } from "./utils/prepareSessionForProfile";

const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  "https://example.com/iseeabunny";

const ProgressDots = ({ totalSteps, currentStep }) => (
  <div className="flex justify-center gap-2">
    {[...Array(totalSteps)].map((_, index) => (
      <div
        key={index}
        className={`w-4 h-4 rounded-full ${
          index < currentStep ? "bg-primary" : "bg-gray-300"
        }`}
      />
    ))}
  </div>
);

const DialogButton = ({
  profile,
  currentDialog,
  onClick,
  readyForUserInput,
}) => {
  const { playButtonClick } = useUISounds();

  if (!readyForUserInput || !currentDialog) return null;

  const buttonImage =
    currentDialog.type === "student"
      ? "https://vvzldbpwpccdljyhgssa.supabase.co/storage/v1/object/public/avatar//bunny.jpeg"
      : profile?.avatar_url;

  return (
    <button
      className="btn btn-primary p-0 overflow-hidden btn-circular-xl"
      onClick={() => {
        playButtonClick();
        onClick();
      }}
    >
      <img
        src={buttonImage}
        alt="tap to continue"
        className="w-[80%] h-[80%] object-cover m-auto rounded-full"
      />
    </button>
  );
};

const CompletionScreen = ({ onStart, isStarting }) => {
  const { playButtonClick } = useUISounds();

  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="text-center p-6 max-w-sm">
        <h2 className="text-2xl font-bold mb-6">
          Your teaching adventure is about to begin!
        </h2>
        <button
          className="btn btn-primary btn-xl max-w-xs w-full"
          onClick={() => {
            playButtonClick();
            onStart();
          }}
          disabled={isStarting}
        >
          {isStarting ? (
            <span className="loading loading-spinner"></span>
          ) : (
            "Start Teaching"
          )}
        </button>
      </div>
    </div>
  );
};

const DialogScreen = ({
  currentDialog,
  currentStep,
  totalSteps,
  readyForUserInput,
  onNext,
  profile,
}) => (
  <div className="h-full w-full flex flex-col landscape:flex-row portrait-container">
    <div className="landscape-section-left portrait-image-container">
      <img
        src={INTRO_CONTENT.images[currentDialog.imageIndex]}
        alt="Intro"
        className="rounded-xl landscape-image portrait-image"
      />
    </div>
    <div className="landscape-section-right portrait-controls">
      <div className="landscape-controls-container">
        <div className="w-full mb-4">
          <ProgressDots totalSteps={totalSteps} currentStep={currentStep} />
        </div>
        <div className="flex justify-center">
          <DialogButton
            profile={profile}
            currentDialog={currentDialog}
            onClick={onNext}
            readyForUserInput={readyForUserInput}
          />
        </div>
      </div>
    </div>
  </div>
);

const AudioTestScreen = ({ onContinue }) => {
  const { playButtonClick } = useUISounds();
  const { playAudioSequence } = useAudioPlayer();
  const [isTestingAudio, setIsTestingAudio] = useState(false);
  const [hasTestedAudio, setHasTestedAudio] = useState(false);

  // Test audio URL
  // Hi there! This is your bunny friend testing the audio. If you can hear my voice clearly, your speakers are working perfectly!
  const testAudioUrl =
    `${MEDIA_BASE_URL}/gen-audio/bunny-testing-speaker.mp3`;

  const handleTestAudio = () => {
    setIsTestingAudio(true);
    playAudioSequence([testAudioUrl], () => {
      setIsTestingAudio(false);
      setHasTestedAudio(true);
    });
  };

  return (
    <div className="h-full w-full flex items-center justify-center">
      <div className="text-center p-6 max-w-md">
        <div className="mb-6">
          <div className="text-6xl mb-4">🔊</div>
          <h2 className="text-2xl font-bold mb-4">Audio Check Required</h2>
          <p className="text-base text-base-content/70 mb-6">
            This app uses audio for an interactive learning experience. Please
            make sure your speakers or headphones are on and the volume is
            audible.
          </p>
        </div>

        <div className="space-y-4">
          <button
            className="btn btn-secondary btn-lg w-full max-w-xs"
            onClick={() => {
              playButtonClick();
              handleTestAudio();
            }}
            disabled={isTestingAudio}
          >
            {isTestingAudio ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Playing Test Audio...
              </>
            ) : (
              <>🎵 Test Audio</>
            )}
          </button>

          {hasTestedAudio && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-4">
              <p className="text-success text-sm">
                ✓ Audio test completed! Did you hear the bunny's voice clearly?
                If yes, you can continue to the introduction.
              </p>
            </div>
          )}

          <button
            className="btn btn-primary btn-lg w-full max-w-xs"
            onClick={() => {
              playButtonClick();
              onContinue();
            }}
          >
            Continue to Introduction
          </button>
        </div>

        <div className="mt-6 text-xs text-base-content/50">
          <p>
            💡 Tip: If you can't hear the test audio, check your device volume
            and audio settings before continuing.
          </p>
        </div>
      </div>
    </div>
  );
};

function IntroApp() {
  const { user } = useSupabaseAuth();
  const { selectedProfile, fetchProfiles } = useProfile();
  const [currentStep, setCurrentStep] = useState(0);
  const [error, setError] = useState("");
  const [readyForUserInput, setReadyForUserInput] = useState(true);
  const { playAudioSequence } = useAudioPlayer();
  const navigate = useNavigate();
  const [showManageOwners, setShowManageOwners] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [showAudioTest, setShowAudioTest] = useState(true);

  const currentDialog = INTRO_CONTENT.dialogs[currentStep];

  const handleNext = () => {
    if (!currentDialog) {
      console.error("handleNext called with no currentDialog available");
      return;
    }

    setReadyForUserInput(false);
    const audioUrl =
      currentDialog.type === "teacher"
        ? currentDialog.audioUrl[selectedProfile?.is_male ? "male" : "female"]
        : currentDialog.audioUrl;

    playAudioSequence([audioUrl], () => {
      setReadyForUserInput(true);
      setCurrentStep((current) => current + 1);
    });
  };

  const handleStart = async () => {
    if (!selectedProfile) {
      console.error("handleStart called with no selectedProfile");
      return;
    }

    try {
      setIsStarting(true);

      // Mark the intro as done in the database
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ intro_done: true })
        .eq("profile_id", selectedProfile.profile_id);

      if (updateError) {
        console.error("Failed to update intro_done status:", updateError);
        throw new Error(updateError.message);
      }

      // Create a new session for this profile
      await prepareSessionForProfile(selectedProfile.profile_id);

      // Refresh profiles to get the updated data
      await fetchProfiles();

      // Navigate back to the main app
      navigate("/");
    } catch (err) {
      console.error("Error in handleStart:", err);
      setError(err.message || "Failed to complete introduction");
    } finally {
      setIsStarting(false);
    }
  };

  if (error) {
    console.error("IntroApp rendering error state:", error);
    return <div className="text-center p-4 text-red-500">{error}</div>;
  }

  if (!user || !selectedProfile) {
    console.warn(
      "IntroApp missing user or selectedProfile, navigating to home"
    );
    navigate("/");
    return null;
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Top avatar menu - positioned absolutely */}
      <div className="absolute top-0 right-0 z-10">
        <ProfileAvatar
          profile={selectedProfile}
          showManageOwners={showManageOwners}
          setShowManageOwners={setShowManageOwners}
          userId={user?.id}
        />
      </div>

      {/* Main content - now takes full height */}
      <div className="h-full w-full">
        {showAudioTest ? (
          <AudioTestScreen onContinue={() => setShowAudioTest(false)} />
        ) : currentDialog ? (
          <DialogScreen
            currentDialog={currentDialog}
            currentStep={currentStep}
            totalSteps={INTRO_CONTENT.dialogs.length}
            readyForUserInput={readyForUserInput}
            onNext={handleNext}
            profile={selectedProfile}
          />
        ) : (
          <CompletionScreen onStart={handleStart} isStarting={isStarting} />
        )}
      </div>
    </div>
  );
}

export default IntroApp;
